import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Upload, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";
import TaxonomyPicker from "@/components/TaxonomyPicker";
import MarkdownEditor from "@/components/MarkdownEditor";

/**
 * Advanced task assignment modal. Handles five targeting modes:
 *   - individual: one assignee
 *   - multiple:   any set of interns/employees via checkboxes
 *   - team:       every member of a Team workspace
 *   - all_interns: broadcast to every intern in the org
 *   - domain:     filter by taxonomy string (matches specialization / expertise
 *                 / employee_role). e.g. "AI/ML", "Computer Science".
 *
 * Employees can only assign to interns; the backend enforces this too but the
 * UI hides non-intern rows to match the mental model.
 */
const PRIORITIES = ["low", "medium", "high"];

export default function BulkAssignTaskModal({ open, onClose, onCreated, currentUser, restrictToInterns = false }) {
  const [mode, setMode] = useState("individual");
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [collaborators, setCollaborators] = useState(new Set());
  const [collabQ, setCollabQ] = useState("");
  const [teamId, setTeamId] = useState("");
  const [domain, setDomain] = useState("");
  const [taxonomyId, setTaxonomyId] = useState(null);
  const [taxonomyPreview, setTaxonomyPreview] = useState({ count: null, sample: [] });
  const [form, setForm] = useState({
    title: "", description: "", priority: "medium", due_date: "", status: "todo",
  });
  const [attachments, setAttachments] = useState([]); // [{filename, url, mime, size, kind}]
  const [subtaskText, setSubtaskText] = useState(""); // one sub-task title per line
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/employee/assignable").then((r) => setUsers(r.data || [])).catch(() => {});
    api.get("/admin/teams").then((r) => setTeams(r.data || [])).catch(() => {});
    // Reset local state when re-opened.
    setMode("individual"); setSelected(new Set()); setCollaborators(new Set()); setCollabQ("");
    setTeamId(""); setDomain(""); setTaxonomyId(null); setTaxonomyPreview({ count: null, sample: [] }); setQ("");
    setForm({ title: "", description: "", priority: "medium", due_date: "", status: "todo" });
    setAttachments([]); setUploading(false); setSubtaskText("");
  }, [open]);

  const filteredUsers = useMemo(() => {
    const t = q.trim().toLowerCase();
    return users.filter((u) => {
      if (restrictToInterns && u.role !== "intern" && !u.is_intern) return false;
      if (!t) return true;
      return (u.name || "").toLowerCase().includes(t)
        || (u.email || "").toLowerCase().includes(t)
        || (u.employee_role || "").toLowerCase().includes(t);
    });
  }, [users, q, restrictToInterns]);

  // Live preview: everyone whose specialization path passes through the
  // selected taxonomy node (or matches the freeform keyword). Runs entirely
  // client-side on the already-fetched assignable list.
  useEffect(() => {
    if (mode !== "domain") return;
    if (!taxonomyId && !domain.trim()) { setTaxonomyPreview({ count: null, sample: [] }); return; }
    const kw = domain.trim().toLowerCase();
    const hits = users.filter((u) => {
      if (taxonomyId) {
        return (u.specialization_path_ids || []).includes(taxonomyId);
      }
      const path = (u.specialization_path || []).join(" › ").toLowerCase();
      return path.includes(kw)
        || (u.specialization || "").toLowerCase().includes(kw)
        || (u.expertise || "").toLowerCase().includes(kw)
        || (u.employee_role || "").toLowerCase().includes(kw);
    });
    setTaxonomyPreview({ count: hits.length, sample: hits.slice(0, 5) });
  }, [mode, taxonomyId, domain, users]);

  const toggleSelected = (uid) => {
    setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  };
  const toggleCollab = (uid) => {
    setCollaborators((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          const r = await api.post("/tasks/attachment-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(r.data);
        } catch (err) {
          toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
        }
      }
      if (uploaded.length) setAttachments((a) => [...a, ...uploaded]);
    } finally { setUploading(false); }
  };

  const iconFor = (a) => {
    if (a.kind === "image" || (a.mime || "").startsWith("image/")) return ImageIcon;
    if (a.kind === "pdf" || a.mime === "application/pdf") return FileText;
    return FileIcon;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    // Resolve targets by mode.
    let assignee_user_ids = null, team_id = null, all_interns = false, domain_val = null;
    if (mode === "individual") {
      if (selected.size !== 1) { toast.error("Pick exactly one assignee"); return; }
      assignee_user_ids = Array.from(selected);
    } else if (mode === "multiple") {
      if (selected.size === 0) { toast.error("Pick at least one assignee"); return; }
      assignee_user_ids = Array.from(selected);
    } else if (mode === "team") {
      if (!teamId) { toast.error("Pick a team"); return; }
      team_id = teamId;
    } else if (mode === "all_interns") {
      all_interns = true;
    } else if (mode === "domain") {
      if (!taxonomyId && !domain.trim()) { toast.error("Pick a taxonomy branch or type a keyword"); return; }
      if (taxonomyId) domain_val = null;
      else domain_val = domain.trim();
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || "",
        priority: form.priority,
        due_date: form.due_date || null,
        status: form.status,
        assignee_user_ids,
        team_id,
        all_interns,
        domain: domain_val,
        taxonomy_id: taxonomyId || null,
        attachments,
        collaborator_user_ids: Array.from(collaborators),
        subtask_titles: subtaskText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const r = await api.post("/tasks/bulk-assign", payload);
      toast.success(`Assigned to ${r.data?.assigned_to || 0} teammate(s)`);
      onCreated && onCreated();
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const modeTab = (k, label) => (
    <button
      key={k}
      type="button"
      onClick={() => { setMode(k); setSelected(new Set()); }}
      className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 border-b-2 transition-colors ${mode === k ? "border-kb-primary text-kb-primary" : "border-transparent text-kb-ink-muted hover:text-kb-ink"}`}
      data-testid={`bulk-assign-mode-${k}`}
    >
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-[170] bg-black/50 flex items-center justify-center p-4" data-testid="bulk-assign-modal">
      <form onSubmit={submit} className="bg-white rounded-sm shadow-2xl max-w-2xl w-full border border-slate-200 max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-serif text-lg text-kb-ink">Assign task</h3>
          <button type="button" onClick={onClose} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pt-3 border-b border-slate-200 flex flex-wrap gap-1">
          {modeTab("individual", "Single")}
          {modeTab("multiple", "Multiple")}
          {modeTab("team", "Team")}
          {modeTab("all_interns", "All interns")}
          {modeTab("domain", "By domain")}
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title *</label>
            <input required className="kb-input mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="bulk-assign-title" />
          </div>

          {/* ---- Target picker per mode ---- */}
          {(mode === "individual" || mode === "multiple") && (
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                {mode === "individual" ? "Assignee *" : "Assignees *"}
                {restrictToInterns && <span className="ml-2 text-kb-ink-light">(interns only)</span>}
              </label>
              <div className="relative mt-1">
                <Search className="w-3.5 h-3.5 text-kb-ink-light absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input className="kb-input pl-8" placeholder="Search name, email or role…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bulk-assign-search" />
              </div>
              <div className="mt-2 border border-slate-200 rounded-sm max-h-52 overflow-y-auto divide-y divide-slate-100">
                {filteredUsers.length === 0 && <div className="p-3 text-xs text-kb-ink-muted">No matching users.</div>}
                {filteredUsers.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                    <input
                      type={mode === "individual" ? "radio" : "checkbox"}
                      name="assignee-picker"
                      checked={selected.has(u.user_id)}
                      onChange={() => {
                        if (mode === "individual") setSelected(new Set([u.user_id]));
                        else toggleSelected(u.user_id);
                      }}
                      className="accent-kb-primary"
                      data-testid={`bulk-assign-user-${u.user_id}`}
                    />
                    <span className="text-sm text-kb-ink flex-1 truncate">{u.name || u.email}</span>
                    <span className="kb-mono text-[10px] text-kb-ink-muted">
                      {u.role}{u.is_intern ? " · intern" : ""}{u.employee_role ? ` · ${u.employee_role}` : ""}
                    </span>
                  </label>
                ))}
              </div>
              {mode === "multiple" && (
                <div className="kb-mono text-[10px] text-kb-ink-muted mt-1">{selected.size} selected</div>
              )}
            </div>
          )}

          {mode === "team" && (
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Team *</label>
              <select required className="kb-input mt-1" value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="bulk-assign-team">
                <option value="">— select a team —</option>
                {teams.map((t) => <option key={t.id} value={t.id}>{t.team_name} ({(t.participants || []).length} members)</option>)}
              </select>
            </div>
          )}

          {mode === "all_interns" && (
            <div className="text-xs text-kb-ink-muted bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
              This will create an individual task row for every intern in the organisation.
            </div>
          )}

          {mode === "domain" && (
            <div className="space-y-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Pick a taxonomy branch</label>
              <p className="kb-mono text-[10px] text-kb-ink-muted">
                Cascades deeper as you drill in. Selecting <em>any</em> node auto-includes every user whose specialization sits under that branch — e.g. picking <em>Computer Science</em> matches interns whose profile is set to <em>Computer Science › AI/ML › LLM</em>.
              </p>
              <TaxonomyPicker domain="both" value={taxonomyId} onChange={setTaxonomyId} />

              <div className="pt-2">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">…or a freeform keyword</label>
                <input
                  className="kb-input mt-1"
                  placeholder="e.g. AI/ML, marketing, LLM"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  data-testid="bulk-assign-domain"
                />
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">
                  Used only when no taxonomy branch is picked. Regex-matches <em>specialization</em>, <em>expertise</em> and <em>employee_role</em>.
                </p>
              </div>

              {taxonomyPreview.count !== null && (
                <div className={`mt-2 rounded-sm px-3 py-2 text-xs border ${taxonomyPreview.count > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="bulk-assign-domain-preview">
                  {taxonomyPreview.count === 0 ? (
                    <>No teammates match this selection yet. They can update their specialization in their profile.</>
                  ) : (
                    <>
                      <strong>{taxonomyPreview.count}</strong> teammate{taxonomyPreview.count === 1 ? "" : "s"} will receive this task.
                      {taxonomyPreview.sample.length > 0 && (
                        <span className="text-kb-ink-muted"> Sample: {taxonomyPreview.sample.map((u) => u.name || u.email).join(", ")}{taxonomyPreview.count > taxonomyPreview.sample.length ? ", …" : ""}</span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ---- Common task fields ---- */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Status</label>
              <select className="kb-input mt-1" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="todo">To do</option>
                <option value="in_progress">In progress</option>
                <option value="review">Review</option>
                <option value="done">Done</option>
              </select>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Priority</label>
              <select className="kb-input mt-1" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Deadline</label>
              <input type="date" className="kb-input mt-1" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="bulk-assign-due" />
            </div>
          </div>

          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Detailed instructions (markdown supported)</label>
            <div className="mt-1">
              <MarkdownEditor
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                placeholder="What needs to be done, deliverables, acceptance criteria… Use the toolbar to format."
                rows={6}
                testId="bulk-assign-description"
              />
            </div>
          </div>

          {/* ---- Sub-tasks (optional). One title per line — each becomes a
                  workspace kanban card the assignee sees in the Sub Tasks tab. */}
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Sub-tasks (optional)</label>
            <textarea
              rows={3}
              className="kb-input mt-1 font-mono text-sm"
              value={subtaskText}
              onChange={(e) => setSubtaskText(e.target.value)}
              placeholder={"One sub-task per line — e.g.\nDesign schema\nWrite tests\nDeploy staging"}
              data-testid="bulk-assign-subtasks"
            />
            <div className="kb-mono text-[10px] text-kb-ink-light mt-1">
              {subtaskText.split("\n").filter((s) => s.trim()).length} sub-task(s) will land in the assignee&apos;s Sub Tasks tab. More can be added later inside the workspace.
            </div>
          </div>

          {/* ---- Attachments (direct upload) ---- */}
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Attachments</label>
            <label className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-kb-primary bg-slate-50 hover:bg-kb-subtle rounded-sm px-4 py-3 cursor-pointer transition-colors" data-testid="bulk-assign-attach-drop">
              <Upload className="w-4 h-4 text-kb-ink-muted" />
              <span className="text-xs text-kb-ink-muted">
                {uploading ? "Uploading…" : "Click to upload images, PDFs, Word docs, etc. (max 5MB each)"}
              </span>
              <input
                type="file"
                multiple
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                data-testid="bulk-assign-attach-input"
              />
            </label>
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1">
                {attachments.map((a, i) => {
                  const Icon = iconFor(a);
                  return (
                    <li key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
                      <Icon className="w-3.5 h-3.5 text-kb-ink-muted shrink-0" />
                      <span className="truncate flex-1">{a.filename}</span>
                      {typeof a.size === "number" && <span className="kb-mono text-[10px] text-kb-ink-light">{(a.size / 1024).toFixed(1)} KB</span>}
                      <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700">
                        <X className="w-3 h-3" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ---- Collaborators (watchers) ---- */}
          {(mode === "individual" || mode === "multiple" || mode === "team") && (
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Collaborators (watchers)</label>
              <div className="relative mt-1">
                <Search className="w-3.5 h-3.5 text-kb-ink-light absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  className="kb-input pl-8"
                  placeholder="Search name, email or role…"
                  value={collabQ}
                  onChange={(e) => setCollabQ(e.target.value)}
                  data-testid="bulk-assign-collab-search"
                />
              </div>
              {collaborators.size > 0 && (
                <div className="kb-mono text-[10px] text-kb-ink-muted mt-1">{collaborators.size} selected</div>
              )}
              <div className="mt-2 border border-slate-200 rounded-sm max-h-40 overflow-y-auto divide-y divide-slate-100">
                {(() => {
                  const t = collabQ.trim().toLowerCase();
                  const pool = users.filter((u) => !selected.has(u.user_id));
                  // Always show already-selected watchers first so they never disappear behind a filter.
                  const picked = pool.filter((u) => collaborators.has(u.user_id));
                  const rest = pool.filter((u) => !collaborators.has(u.user_id)).filter((u) => {
                    if (!t) return true;
                    return (u.name || "").toLowerCase().includes(t)
                      || (u.email || "").toLowerCase().includes(t)
                      || (u.role || "").toLowerCase().includes(t)
                      || (u.employee_role || "").toLowerCase().includes(t);
                  });
                  const rows = [...picked, ...rest.slice(0, 100)];
                  if (rows.length === 0) return <div className="p-3 text-xs text-kb-ink-muted">No matching users.</div>;
                  return rows.map((u) => (
                    <label key={u.user_id} className="flex items-center gap-2 px-3 py-1 hover:bg-slate-50 cursor-pointer text-xs">
                      <input type="checkbox" checked={collaborators.has(u.user_id)} onChange={() => toggleCollab(u.user_id)} className="accent-kb-primary" data-testid={`bulk-assign-collab-${u.user_id}`} />
                      <span className="flex-1 truncate">{u.name || u.email}</span>
                      <span className="kb-mono text-[9px] text-kb-ink-light">{u.role}{u.employee_role ? ` · ${u.employee_role}` : ""}</span>
                    </label>
                  ));
                })()}
              </div>
              {!collabQ && users.filter((u) => !selected.has(u.user_id) && !collaborators.has(u.user_id)).length > 100 && (
                <div className="kb-mono text-[10px] text-kb-ink-light mt-1">Showing first 100 — refine the search to see more.</div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="kb-btn-outline text-xs" disabled={busy}>Cancel</button>
          <button type="submit" className="kb-btn-primary text-xs" disabled={busy} data-testid="bulk-assign-submit">
            {busy ? "Assigning…" : "Assign task"}
          </button>
        </div>
      </form>
    </div>
  );
}
